<template>
  <Transition name="opacity" appear @before-enter="showDrawer = true">
    <div
      data-testid="studio-drawer"
      class="tw-fixed tw-inset-0 tw-z-50 tw-overflow-hidden tw-bg-white/90 dark:tw-bg-black/90"
      @click="handleOutsideClick"
    >
      <Transition name="fly-from-right" appear @after-leave="$emit('cancel')">
        <div
          v-if="showDrawer"
          :class="`tw-fixed tw-inset-0 tw-m-2 tw-flex tw-flex-col tw-overflow-x-auto tw-rounded-xl tw-border tw-bg-white tw-shadow-[0_0_5px_5px_rgba(0,0,0,0.05)] dark:tw-bg-black dark:tw-shadow-[0_0_5px_5px_#11182766] ${
            extraClasses ? extraClasses : ''
          }`"
          @click.stop
        >
          <div
            class="tw-z-50 tw-flex tw-items-center tw-justify-between tw-border-b tw-bg-white tw-py-4 tw-pl-5 tw-pr-4 dark:tw-bg-black"
          >
            <div class="tw-flex tw-items-center tw-gap-3">
              <div class="dark:tw-text-white">{{ title }}</div>
              <slot name="header" />
            </div>
            <button
              v-if="crossIcon"
              data-testid="close-studio-drawer"
              :disabled="cancelDisabled"
              @click="handleCancelClicked"
            >
              <Cross class="tw-h-6 tw-w-6 tw-cursor-pointer dark:tw-text-white" />
            </button>
            <div v-else class="tw-flex tw-space-x-2">
              <StudioButton
                v-if="showCancelButton"
                data-testid="cancel-studio-drawer"
                :disabled="cancelDisabled"
                :disabledReason="cancelButtonDisabledReason"
                trackingId="studio-drawer-cancel-button"
                variant="text"
                @click="handleCancelClicked"
              >
                Cancel
              </StudioButton>
              <StudioButton
                v-if="!hidePrimaryButton"
                data-testid="primary-studio-drawer-button"
                :disabled="primaryButtonDisabled"
                :disabledReason="primaryButtonDisabledReason"
                trackingId="studio-drawer-primary-button"
                type="submit"
                variant="filled"
                @click="$emit('primaryButtonClick')"
              >
                {{ primaryButtonText }}
              </StudioButton>
            </div>
          </div>
          <div class="tw-flex tw-h-full tw-flex-col tw-overflow-auto">
            <slot />
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { Cross } from './Icons';
import StudioButton from './StudioButton.vue';

export default defineComponent({
  name: 'StudioDrawer',
  components: {
    StudioButton,
    Cross
  },
  props: {
    title: {
      type: String,
      required: true
    },
    primaryButtonText: {
      type: String,
      default: 'Save'
    },
    primaryButtonDisabledReason: {
      type: String,
      default: ''
    },
    primaryButtonDisabled: {
      type: Boolean,
      default: false
    },
    hidePrimaryButton: {
      type: Boolean,
      default: false
    },
    cancelDisabled: {
      type: Boolean,
      default: false
    },
    cancelButtonDisabledReason: {
      type: String,
      default: ''
    },
    extraClasses: {
      type: String,
      default: ''
    },
    preventClose: {
      type: Boolean,
      default: false
    },
    preventCloseMessage: {
      type: String,
      default: 'You have unsaved changes. Are you sure you want to discard them?'
    },
    crossIcon: {
      type: Boolean,
      default: false
    },
    showCancelButton: {
      type: Boolean,
      default: true
    },
    forcePreventClose: {
      type: Boolean,
      default: false
    },
    closeOnOutsideClick: {
      type: Boolean,
      default: true
    },
    closeOnEscape: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showDrawer: false
    };
  },
  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    window.addEventListener('keydown', this.handleKeyDown);
    document.documentElement.style.overscrollBehaviorX = 'none';
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    window.removeEventListener('keydown', this.handleKeyDown);
    document.documentElement.style.overscrollBehaviorX = '';
  },
  methods: {
    handleCancelClicked() {
      if (this.forcePreventClose) {
        this.$emit('onForcePreventClose');
        return;
      }

      if (this.preventClose && !window.confirm(this.preventCloseMessage)) {
        return;
      }
      this.showDrawer = false;
    },
    handleOutsideClick() {
      if (!this.closeOnOutsideClick) {
        return;
      }

      this.handleCancelClicked();
    },
    handleBeforeUnload(e: BeforeUnloadEvent) {
      if (this.preventClose) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (!this.closeOnEscape) {
          return;
        }
        e.preventDefault();
        this.handleCancelClicked();
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'ArrowLeft') {
        if (!this.closeOnEscape) {
          return;
        }
        e.preventDefault();
        this.handleCancelClicked();
      }
    },
    /** @public */
    closeDrawer() {
      this.showDrawer = false;
    }
  }
});
</script>

<style lang="scss" scoped>
.fly-from-right-enter-active,
.fly-from-right-leave-active {
  transition: transform 0.25s;
}
.fly-from-right-enter-from,
.fly-from-right-leave-to {
  transform: translateX(100%);
}

.opacity-enter-active,
.opacity-leave-active {
  transition: opacity 0.25s;
}

.opacity-enter-from,
.opacity-leave-to {
  opacity: 0;
}
</style>
